"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { PDFDocument } from "pdf-lib";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getSiteContent } from "@/lib/site-content";



export function MergePdf() {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const content = getSiteContent(locale).toolsPage;
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFilesSelected = (selectedFiles: File[]) => {
    if (selectedFiles.length > 0) {
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const moveFileUp = (index: number) => {
    if (index === 0) return;
    const newFiles = [...files];
    [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
    setFiles(newFiles);
  };

  const moveFileDown = (index: number) => {
    if (index === files.length - 1) return;
    const newFiles = [...files];
    [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
    setFiles(newFiles);
  };

  const mergePDFs = async () => {
    if (files.length < 2) return;

    setIsProcessing(true);
    setProgress(0);

    try {
      const mergedPdf = await PDFDocument.create();

      for (let i = 0; i < files.length; i++) {
        const fileBytes = await files[i].arrayBuffer();
        const pdf = await PDFDocument.load(fileBytes);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `merged-${Date.now()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error merging PDFs:", error);
      alert(content.mergePdfError);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:information-variant" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{content.mergePdfTitle}</p>
            <p className="leading-relaxed">{content.mergePdfDescription}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <FileUploader 
          accept=".pdf" 
          multiple
          onFilesSelected={handleFilesSelected}
        />
        <p className="text-sm text-gray-500 text-center">{content.mergePdfHint}</p>
      </div>

      {files.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-positivus-dark">{content.filesToMerge} ({files.length})</h3>
            <button 
                onClick={() => setFiles([])}
                className="text-sm text-red-500 hover:text-red-600 font-medium"
            >
                {content.clearAll}
            </button>
          </div>

          <div className="space-y-3">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-4 rounded-[20px] border-2 border-positivus-dark bg-white p-4"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveFileUp(index)}
                    disabled={index === 0}
                    className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={content.moveUp}
                  >
                    <Icon icon="mdi:chevron-up" className="h-5 w-5 text-positivus-dark" />
                  </button>
                  <button
                    onClick={() => moveFileDown(index)}
                    disabled={index === files.length - 1}
                    className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={content.moveDown}
                  >
                    <Icon icon="mdi:chevron-down" className="h-5 w-5 text-positivus-dark" />
                  </button>
                </div>

                <div className="flex h-12 w-12 items-center justify-center rounded-[15px] bg-red-100 text-red-600 border border-red-200">
                   <Icon icon={uiIcons.fileText} className="h-6 w-6" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-positivus-dark truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>

                <button
                  onClick={() => removeFile(index)}
                  className="rounded-full p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Icon icon={uiIcons.close} className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {files.length >= 2 && (
        <button
          onClick={mergePDFs}
          disabled={isProcessing}
          className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-positivus-dark disabled:hover:text-white"
        >
          {isProcessing ? `${content.merging} ${progress}%` : content.mergePdfs}
        </button>
      )}

      {isProcessing && (
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden border border-positivus-dark/20">
          <div
            className="h-full bg-positivus-green transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
